import { useState, useEffect, useRef } from 'react'
import { formatPhoneInput, toE164, isValidPhone } from '../../utils/formatPhone'
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import ResponsiveSheet from '../common/ResponsiveSheet'
import { User, usersApi, settingsApi, ApiError } from '../../services/api'

// The order the fields are read, which is the order a validation failure walks.
const FIELD_ORDER: (keyof NewClientData)[] = ['name', 'email', 'phone']

interface AddClientDialogProps {
  open: boolean
  onClose: () => void
  onAdd: (client: NewClientData) => Promise<void>
}

export interface NewClientData {
  name: string
  email: string
  phone: string
  assignedTo?: string
  source?: string
}

export default function AddClientDialog({ open, onClose, onAdd }: AddClientDialogProps) {
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down(768))
  const [formData, setFormData] = useState<NewClientData>({
    name: '',
    email: '',
    phone: '',
    assignedTo: '',
    source: 'direct',
  })

  const [errors, setErrors] = useState<Partial<Record<keyof NewClientData, string>>>({})
  // A failure that is not about one field. Kept apart from `errors` so it never
  // competes with a field message for the same spot.
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)

  const nameRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const phoneRef = useRef<HTMLInputElement>(null)
  const fieldRefs: Record<keyof NewClientData, React.RefObject<HTMLInputElement | null>> = {
    name: nameRef,
    email: emailRef,
    phone: phoneRef,
    assignedTo: nameRef,
    source: nameRef,
  }

  useEffect(() => {
    if (open) {
      loadUsers()
    }
  }, [open])

  const loadUsers = async () => {
    try {
      setLoadingUsers(true)
      const [usersData, defaultOwnerSetting] = await Promise.all([
        usersApi.list({ status: 'active' }),
        settingsApi.get('defaultClientSuccessRep').catch(() => ({ key: 'defaultClientSuccessRep', value: null })),
      ])
      // Filter to only show users who can own clients
      const eligibleUsers = (usersData || []).filter((u) =>
        ['admin', 'manager', 'client_success'].includes(u.role)
      )
      setUsers(eligibleUsers)

      // Set default owner if configured and user hasn't already selected one
      if (defaultOwnerSetting?.value && !formData.assignedTo) {
        setFormData((prev) => ({ ...prev, assignedTo: defaultOwnerSetting.value || '' }))
      }
    } catch (err) {
      console.error('Failed to load users:', err)
    } finally {
      setLoadingUsers(false)
    }
  }

  const handleChange = (field: keyof NewClientData, value: string) => {
    setFormData({ ...formData, [field]: value })
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined })
    }
  }

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof NewClientData, string>> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }

    setErrors(newErrors)

    // Send focus to the first field that failed, in the order they are read.
    // Marking a field red below the fold tells someone nothing if the form is
    // taller than the sheet they are looking at.
    const firstInvalid = FIELD_ORDER.find((f) => newErrors[f])
    if (firstInvalid) {
      fieldRefs[firstInvalid].current?.focus()
    }

    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    setSubmitError(null)
    if (!validate()) return

    setSubmitting(true)
    try {
      await onAdd({ ...formData, phone: formData.phone ? toE164(formData.phone) : '' })
      setFormData({
        name: '',
        email: '',
        phone: '',
        assignedTo: '',
        source: 'direct',
      })
      onClose()
    } catch (err) {
      // Nothing typed is cleared here, deliberately. A failed create leaves the
      // dialog exactly as it was so the fix is one edit, not a re-entry.
      if (err instanceof ApiError && err.status === 409 && err.field) {
        setErrors((prev) => ({ ...prev, [err.field as keyof NewClientData]: err.message }))
        fieldRefs[err.field as keyof NewClientData]?.current?.focus()
      } else {
        setSubmitError(
          err instanceof ApiError && err.message
            ? err.message
            : 'The client could not be created. Nothing has been saved — check the details above and try again.'
        )
      }
      console.error('Failed to add client:', err)
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      assignedTo: '',
      source: 'direct',
    })
    setErrors({})
    setSubmitError(null)
    onClose()
  }

  return (
    <ResponsiveSheet
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullScreenBelow={768}
      // The title and the actions stay put; only the fields scroll. On a phone
      // the sheet is the whole screen, so a form that scrolled as one piece
      // would carry its submit button off the bottom.
      PaperProps={{ sx: { display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle sx={{ fontSize: '1rem', lineHeight: '1.375rem', fontWeight: 600 }}>
        Add New Client
      </DialogTitle>
      <DialogContent dividers={isPhone}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {submitError && (
            <Alert severity="error" onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          <TextField
            label="Name"
            inputRef={nameRef}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            required
          />

          <TextField
            label="Email"
            type="email"
            inputRef={emailRef}
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
            required
          />

          <TextField
            label="Phone"
            inputRef={phoneRef}
            value={formatPhoneInput(formData.phone)}
            onChange={(e) => handleChange('phone', formatPhoneInput(e.target.value))}
            fullWidth
            error={!!(errors.phone || (formData.phone && !isValidPhone(formData.phone)))}
            helperText={errors.phone || (formData.phone && !isValidPhone(formData.phone) ? 'Enter a valid 10-digit phone number' : '')}
            placeholder="(555) 123-4567"
          />

          <FormControl fullWidth disabled={loadingUsers}>
            <InputLabel>Owner</InputLabel>
            <Select
              value={formData.assignedTo || ''}
              label="Owner"
              onChange={(e) => handleChange('assignedTo', e.target.value)}
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {users.map((user) => (
                <MenuItem key={user.email} value={user.email}>
                  {user.name} ({user.role})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 2 }}>
        <Button onClick={handleClose} disabled={submitting} sx={{ minHeight: 44 }}>
          Cancel
        </Button>
        {/* The label stays while it works. Swapping it for a spinner resized
            the button mid-click and left nothing saying what was happening. */}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ minHeight: 44 }}
        >
          {submitting ? 'Adding…' : 'Add Client'}
        </Button>
      </DialogActions>
    </ResponsiveSheet>
  )
}
